import React, { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import Login from "./Login";
import App from "./App";
import ResetPassword from "./ResetPassword";
import PublicView from "./PublicView";
import { wsApi } from "./api";
import { resetRegistry, buildWinMedikaSeed, WINMEDIKA_SEED_ID, WINMEDIKA_SEED_NAME } from "./data";

const KEYS = [
  "strukturku.catColors", "strukturku.companyCat", "strukturku.companyOps",
  "strukturku.primaryKbli", "strukturku.docExtra", "strukturku.customGroups",
  "strukturku.currentGroup", "strukturku.groupNames", "strukturku.hiddenOwners",
  "strukturku.deletedGroups", "strukturku.groupData", "strukturku.seeded",
];

const OBJ_MAP: Record<string, string> = {
  catColors: "strukturku.catColors",
  companyCat: "strukturku.companyCat",
  companyOps: "strukturku.companyOps",
  primaryKbli: "strukturku.primaryKbli",
  docExtra: "strukturku.docExtra",
  customGroups: "strukturku.customGroups",
  groupNames: "strukturku.groupNames",
  hiddenOwnersMap: "strukturku.hiddenOwners",
  deletedGroups: "strukturku.deletedGroups",
  groupData: "strukturku.groupData",
  seeded: "strukturku.seeded",
};

// Muat workspace user dari server → hidrasi localStorage sebelum App di-mount.
function WorkspaceLoader({ userId }: { userId: string }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      resetRegistry();
      KEYS.forEach((k) => localStorage.removeItem(k));
      let state: any = null;
      try {
        const res = await wsApi.get();
        state = res.state;
        if (state && typeof state === "object") {
          for (const [field, key] of Object.entries(OBJ_MAP)) {
            if (state[field] !== undefined) localStorage.setItem(key, JSON.stringify(state[field]));
          }
          if (typeof state.currentGroupId === "string") localStorage.setItem("strukturku.currentGroup", state.currentGroupId);
        }
      } catch { /* mulai dari default bila gagal */ }

      // Seed data contoh Win Medika ke ruang kerja user saat pertama login.
      const alreadySeeded = state && typeof state === "object" && state.seeded === true;
      let groups: any[] = [];
      try { groups = JSON.parse(localStorage.getItem("strukturku.customGroups") || "[]"); } catch { groups = []; }
      if (!alreadySeeded && (!groups || groups.length === 0)) {
        const seed = buildWinMedikaSeed();
        localStorage.setItem("strukturku.groupData", JSON.stringify({ [WINMEDIKA_SEED_ID]: seed }));
        localStorage.setItem("strukturku.customGroups", JSON.stringify([{ id: WINMEDIKA_SEED_ID, name: WINMEDIKA_SEED_NAME }]));
        localStorage.setItem("strukturku.currentGroup", WINMEDIKA_SEED_ID);
      }
      localStorage.setItem("strukturku.seeded", "true");
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [userId]);

  if (!ready) {
    return (
      <div className="auth-wrap" data-theme="light">
        <div className="auth-loading serif" data-testid="ws-loading">Memuat ruang kerja…</div>
      </div>
    );
  }
  return <App key={userId} />;
}

export default function Root() {
  const { user } = useAuth();

  // Rute publik tampilan-saja (tanpa login): /share/<token>
  const shareMatch = window.location.pathname.match(/\/share\/([^/?#]+)/);
  if (shareMatch) {
    return <PublicView token={decodeURIComponent(shareMatch[1])} />;
  }

  // Rute reset password (dari link email) — tampil tanpa perlu login.
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("token");
  if (window.location.pathname.includes("reset-password") || resetToken) {
    return <ResetPassword token={resetToken} />;
  }

  if (user === undefined) {
    return (
      <div className="auth-wrap" data-theme="light">
        <div className="auth-loading serif" data-testid="auth-loading">Memeriksa sesi…</div>
      </div>
    );
  }
  if (!user) return <Login />;
  return <WorkspaceLoader userId={user.id} />;
}
